import { useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { Loader2, Search, X } from "lucide-react";
import { lookupTicketByDisplayNumber, parseTicketQuery } from "@/lib/tickets";
import { cn } from "@/lib/utils";

/**
 * "Go to request 412", next to the queue's filters.
 *
 * Takes either identifier, because somebody pasting one out of an email has no reason to
 * know which of the two they are holding: the readable `412` (or `#412`) and the opaque
 * `TF-7K2M-9QX4` both resolve here.
 *
 * ⚠️ This is a JUMP, not a filter, and the distinction is deliberate. It does not narrow
 * the queue behind it — narrowing a list to one row hides the queue somebody was reading
 * and needs undoing afterwards, whereas a jump leaves it exactly as it was to come back to.
 *
 * ⚠️ It searches on ENTER, never while typing, and that is not about saving requests.
 * Every resolved lookup is recorded as a PHI read against the caller's name, because
 * §164.312(b) requires it. Searching on each keystroke would file "read ticket 4" and
 * "read ticket 41" on the way to 412 — three entries in a HIPAA audit trail for two
 * tickets nobody asked to see, against a person who did nothing wrong. A debounce narrows
 * that window; it does not close it. Submitting does.
 */
export function TicketQuickSearch() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  // ⚠️ One message for every failure, and it is phrased as a fact about the SEARCH rather
  // than about the ticket. "No request matches" is true whether the number was never
  // issued or names a request this person cannot read — and the server cannot tell the
  // caller which, by design, because that difference is the enumeration oracle. Saying
  // "you do not have access to that one" here would rebuild in the browser exactly what
  // the 404 was written to prevent.
  const [missed, setMissed] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();

    const parsed = parseTicketQuery(value);
    if (!parsed || busy) {
      setMissed(parsed === null && value.trim() !== "");
      return;
    }

    setMissed(false);

    // The opaque number already addresses a ticket, so there is nothing to resolve —
    // going straight there saves a round trip, and the ticket page returns the same 404
    // for a number that is unknown as for one that is not this caller's.
    if (parsed.kind === "number") {
      setValue("");
      void navigate({ to: "/tickets/$id", params: { id: parsed.value } });
      return;
    }

    setBusy(true);
    const found = await lookupTicketByDisplayNumber(parsed.value);
    setBusy(false);

    if (!found) {
      setMissed(true);
      inputRef.current?.select();
      return;
    }

    setValue("");
    void navigate({ to: "/tickets/$id", params: { id: found.number } });
  }

  return (
    <form onSubmit={(e) => void submit(e)} className="relative">
      <label htmlFor="ticket-quick-search" className="sr-only">
        Go to a request by number
      </label>

      <Search
        className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />

      <input
        id="ticket-quick-search"
        ref={inputRef}
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setMissed(false);
        }}
        // inputMode, not type="number": the box also takes TF-7K2M-9QX4, and a number
        // input would refuse the letters while offering spinners nobody wants.
        inputMode="text"
        autoComplete="off"
        placeholder="Go to #…"
        aria-describedby={missed ? "ticket-quick-search-missed" : undefined}
        aria-invalid={missed || undefined}
        className={cn(
          "h-9 w-36 rounded-lg border bg-background pl-8 pr-7 text-sm transition",
          "focus:border-ring focus:outline-none sm:w-44",
          missed ? "border-destructive/60" : "border-input",
        )}
      />

      {busy ? (
        <Loader2
          className="absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-muted-foreground"
          aria-hidden
        />
      ) : (
        value !== "" && (
          <button
            type="button"
            onClick={() => {
              setValue("");
              setMissed(false);
              inputRef.current?.focus();
            }}
            aria-label="Clear"
            className="absolute right-1.5 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded text-muted-foreground transition hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        )
      )}

      {missed && (
        // role="status" rather than "alert": not finding a ticket is an ordinary outcome
        // of a search, and an assertive announcement on every mistyped number would make
        // the box hostile to a screen-reader user.
        <p
          id="ticket-quick-search-missed"
          role="status"
          className="absolute left-0 top-full z-10 mt-1 whitespace-nowrap rounded-lg border border-border bg-card px-2 py-1 text-xs text-muted-foreground shadow-sm"
        >
          No request matches that number.
        </p>
      )}
    </form>
  );
}
